/**
 * Admin API Service
 * Centralized service for checking organization admin settings
 * Used to determine if "Send To Admin API" feature is enabled
 */

import { BASE_URL } from "../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../API_Endpoints/APIEndpoints";

/**
 * Check if Send To Admin API is enabled in organization settings
 * @returns Promise<boolean> - true if enabled, false otherwise
 */
export const isAdminApiEnabled = async (): Promise<boolean> => {
  try {
    const userData = getUserIdFromLocalStorage();

    if (!userData || !userData.token || !userData.userId || !userData.organizationId) {
      console.log("❌ No user data available for Admin API check");
      return false;
    }

    const { userId, organizationId, token } = userData;

    const response = await fetch(
      `${BASE_URL}/OrangizationConfiguration/${userId}/${organizationId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.ok) {
      const data = await response.json();

      // Find the current organization (try by id first, then use first element)
      let currentOrg = data.find((org: any) => org.id === organizationId);
      if (!currentOrg && data.length > 0) {
        currentOrg = data[0];
      }

      if (currentOrg) {
        const isEnabled =
          currentOrg.sendToAdminURL === "true" || currentOrg.sendToAdminURL === true;
        console.log(`✅ Admin API enabled: ${isEnabled}`);
        return isEnabled;
      }
    }

    console.log("❌ Admin API not enabled or organization not found");
    return false;
  } catch (error) {
    console.error("❌ Error checking Admin API status:", error);
    return false;
  }
};

/**
 * Get organization configuration details
 * @returns Promise with organization config or null
 */
export const getOrganizationConfig = async (): Promise<any | null> => {
  try {
    const userData = getUserIdFromLocalStorage();

    if (!userData || !userData.token || !userData.userId || !userData.organizationId) {
      return null;
    }

    const { userId, organizationId, token } = userData;

    const response = await fetch(
      `${BASE_URL}/OrangizationConfiguration/${userId}/${organizationId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.ok) {
      const data = await response.json();

      // Find the current organization
      let currentOrg = data.find((org: any) => org.id === organizationId);
      if (!currentOrg && data.length > 0) {
        currentOrg = data[0];
      }

      return currentOrg;
    }

    return null;
  } catch (error) {
    console.error("Error fetching organization config:", error);
    return null;
  }
};

/**
 * Get Comply Admin URL from organization settings
 * @returns Promise<string> - Comply Admin URL or empty string
 */
export const getComplyAdminUrl = async (): Promise<string> => {
  const config = await getOrganizationConfig();
  return config?.complyAdminUrl || "";
};

/**
 * Get auto-suspend days setting
 * @returns Promise<number> - Number of days or 0 if disabled
 */
export const getAutoSuspendDays = async (): Promise<number> => {
  const config = await getOrganizationConfig();
  return config?.autoSuspendUserAfterDays || 0;
};

export default {
  isAdminApiEnabled,
  getOrganizationConfig,
  getComplyAdminUrl,
  getAutoSuspendDays,
};
